import React, { FC, forwardRef, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as TopArrow } from "images/top-arrow.svg";
import { Radio } from "components-new";
import { getActivities, getObjects } from "toolkit/steps/stepFive/stepFive.thunks";
import Tooltip from "components-new/Tooltip";
import SureModal from "../SureModal/SureModal";
import Badge from "components-new/Badge";
import cn from "classnames";
import styles from "./Table.module.sass";
import { IBadge } from "components-new/Badge/Badge";

const BUDGE_TYPES = ["Утверждено", "Частично утверждено", "Не утверждено"];

const DropDown: FC<{
	type: string;
	options: string[];
	handleBy: (sort: any, type: any) => void;
}> = ({ type, options, handleBy }) => {
	const sortBy = useAppSelector((state) => state.stepFive.sortBy);

	return (
		<div className={styles.dropDownOptions} onClick={(e) => e.stopPropagation()}>
			<div className={styles.option}>
				<Radio
					label="Все"
					value="all"
					name="all"
					checked={sortBy[type].id === 0}
					handleSelected={() => {
						handleBy(0, type);
					}}
				/>
			</div>
			{options.map((option: any) => (
				<div key={option.name} className={styles.option}>
					<Radio
						label={option.name}
						handleSelected={() => {
							handleBy(option.id, type);
						}}
						name={option.name}
						checked={sortBy[type].id === option.id}
						value={option.id}
					/>
				</div>
			))}
		</div>
	);
};

interface TableProps {
	data: any[];
	onClickModelFormed: (id: number, objectName: string, row: any) => void;
	handleBy: (sort: any, type: any) => void;
	sureModalVisible: boolean;
	showSureModal: () => void;
	close: () => void;
	lastTargetRef: React.MutableRefObject<HTMLDivElement | null>;
}

const Table = forwardRef<HTMLDivElement | null, TableProps>(
	({ data, handleBy, onClickModelFormed, sureModalVisible, showSureModal, close, lastTargetRef }, ref) => {
		const dispatch = useAppDispatch();
		const location = useLocation();
		const [selected, setSelected] = useState<any>();

		const navigate = useNavigate();

		const { organizationId } = useParams();

		const [isShowDropObjects, setIsShowDropObjects] = useState<boolean>(false);
		const [isShowDropAcitvities, setIsShowDropActivities] = useState<boolean>(false);
		const [isShowDropStatuses, setIsShowDropStatuses] = useState<boolean>(false);

		const { objects, activities, statuses } = useAppSelector((state) => state.stepFive);

		useEffect(() => {
			dispatch(getActivities());
			dispatch(getObjects());

			const handleHide = () => {
				setIsShowDropActivities(false);
				setIsShowDropObjects(false);
				setIsShowDropStatuses(false);
			};

			document.addEventListener("click", handleHide);

			return () => {
				document.removeEventListener("click", handleHide);
			};
			//eslint-disable-next-line
		}, []);

		const redirectFunction = (row: any) => {
			if ((row.threatModelObjectExists && !row.categoryAssigned) || row.repeatable) {
				if (row.repeatable) {
					showSureModal();
					setSelected(row);
				} else {
					onClickModelFormed(row.id, row.objectName, row);
				}
			} else if (!row.threatModelObjectExists && !row.categoryAssigned) {
				navigate(`/categorization/newModel/${organizationId}/${row.id}`, {
					state: {
						actId: row.topLevelKiiObjActDocId,
						resId: row.topLevelKiiObjResultDocId,
					},
				});
			} else if (row.categoryAssigned && !row.threatModelObjectExists && !row.repeatable) {
				navigate(`/categorization/newModel/${organizationId}/${row.id}`, {
					state: {
						actId: row.topLevelKiiObjActDocId,
						resId: row.topLevelKiiObjResultDocId,
					},
				});
			} else {
				navigate(`/categorization/finish/${organizationId}/${row.id}`, {
					state: {
						objectName: row.objectName,
						category: row.category,
						actId: row.topLevelKiiObjActDocId,
						resId: row.topLevelKiiObjResultDocId,
					},
				});
			}
		};

		const getBadgeType = (num: number): IBadge["type"] => {
			if (num === 3) {
				return "error";
			} else if (num === 2) {
				return "warn";
			} else if (num === 1) {
				return "success";
			}
		};

		const circleColor = (repeatable: boolean, category: boolean, threat: boolean) => {
			if (repeatable) return styles.purple;
			if (!category && !threat) return styles.white;
			if (category) return styles.green;
			if (threat) return styles.blue;
		};

		return (
			<div className={styles.table} ref={ref}>
				<div className={styles.thead}>
					<div className={styles.tr}>
						<div>№</div>
						<div>Код</div>
						<div>Наименование</div>
						<div
							className={styles.dropDown}
							onClick={(e) => {
								e.stopPropagation();
								setIsShowDropObjects((prevState: any) => !prevState);
								setIsShowDropStatuses(false);
								setIsShowDropActivities(false);
							}}>
							<div>Тип объекта</div>
							<TopArrow
								className={cn(styles.dropDownIcon, {
									[styles.dropDownIconActive]: isShowDropObjects,
								})}
							/>
							{isShowDropObjects && <DropDown type="objects" options={objects} handleBy={handleBy} />}
						</div>
						<div
							className={styles.dropDown}
							onClick={(e) => {
								e.stopPropagation();
								setIsShowDropActivities((prevState: any) => !prevState);
								setIsShowDropStatuses(false);
								setIsShowDropObjects(false);
							}}>
							<div>Сфера деятельности</div>

							<TopArrow
								className={cn(styles.dropDownIcon, {
									[styles.dropDownIconActive]: isShowDropAcitvities,
								})}
							/>
							{isShowDropAcitvities && <DropDown type="activities" options={activities} handleBy={handleBy} />}
						</div>
						<div
							className={styles.dropDown}
							onClick={(e) => {
								e.stopPropagation();
								setIsShowDropStatuses((prevState) => !prevState);
								setIsShowDropActivities(false);
								setIsShowDropObjects(false);
							}}>
							<div>Статус документа</div>
							<TopArrow
								className={cn(styles.dropDownIcon, {
									[styles.dropDownIconActive]: isShowDropStatuses,
								})}
							/>

							{isShowDropStatuses && <DropDown type="statuses" options={statuses} handleBy={handleBy} />}
						</div>
					</div>
				</div>
				<div data-comment-false={true} className={styles.tbody}>
					{data.map((row, index) => {
						let circle = circleColor(row.repeatable, row.categoryAssigned, row.threatModelObjectExists);
						const className = row.id === location.state?.outline ? styles.outline : "";
						return (
							<React.Fragment key={index}>
								<Tooltip
									color="themeWhite"
									// key={row.id}
									menu={
										<div className={styles.tooltip}>
											<div className={styles.tooltipHeader}>
												<div className={styles.tooltipTitle}>Назначение</div>
												<div className={styles.tooltipTitle}>Ответственный</div>
											</div>
											<div className={styles.tooltipInfo}>
												<div className={styles.tooltipText}>{row.purpose}</div>
												<div className={styles.tooltipAuthor}>
													<span> {row.responsible.firstName}</span>
													<span> {row.responsible.middleName}</span>
													<span> {row.responsible.lastName}</span>
												</div>
											</div>
										</div>
									}>
									<div className={cn(styles.tr, className)} key={index} onClick={() => redirectFunction(row)}>
										<div className={styles.circleParent}>
											<div className={cn(styles.circle, circle)} />
											{index + 1}
										</div>
										<div>{row.code}</div>
										<div>{row.objectName}</div>
										<div className={cn(styles.typeObject)}>{row.objectType.name}</div>
										<div>{row.activity.name}</div>
										<div>
											<Badge type={getBadgeType(row.categorizationObjSelectionDocsGroupStatusId)}>
												{BUDGE_TYPES[row.categorizationObjSelectionDocsGroupStatusId - 1]}
											</Badge>
										</div>
									</div>
								</Tooltip>
							</React.Fragment>
						);
					})}
					<div ref={lastTargetRef} />
				</div>
				{sureModalVisible && (
					<SureModal
						close={close}
						callback={() => {
							onClickModelFormed(selected.id, selected.objectName, selected);
						}}
					/>
				)}
			</div>
		);
	},
);

export default Table;
